import React, { useRef } from 'react'
import { Redirect, Route, useHistory } from 'react-router-dom'
import { Security, LoginCallback } from '@okta/okta-react'
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js'
import Login from '../../features/login/page/Login.page'
import Splash from '../../features/splash/page/Splash.page'
import Home from '../../features/home/page/Home.page'
import Demo from '../../features/demo/page/Demo.page'
import Crew from '../../features/crew/page/Crew.page'
import Tabs from '../tabs/Tabs.component'
import DriversLicenseScanner from '../../features/driversLicenseScanner/page/DriversLicenseScanner.page'
import { AppRouterConstants } from './AppRouter.constants'
import { useSelector } from 'react-redux'
import { getUserSelector } from '../../features/user/selectors'
import { AuthenticationService } from '../../features/login/services/authentication.service'
import Landing from '../../features/landing/Landing.page'
import TermsAndConditions from '../../features/signatures/termsAndConditions/termsAndConditions'
import ServiceSelection from '../../features/serviceSelection/page/ServiceSelection.page'
import CameraAttachments from '../../features/cameraAttachments/page/CameraAttachments.page'
import SignaturesMainPage from '../../features/signatures/page/SignaturesMain.page'
import HelpGuide from '../../features/helpGuide/page/HelpGuide.page'
import EKGImportMainPage from '../../features/ekgImport/page/EKGImportMain.page'
import { IonRouterOutlet } from '@ionic/react'
import DocumentScanner from '../../features/documentScanner/page/DocumentScanner.page'
import { isMobileBrowser } from '../../utilities/platform.util'
import SpecialReportsPage from '../../features/specialReports/page/SpecialReports.page'
import FillablePDFsPage from '../../features/fillablePDFs/page/FillablePDFs.page'
import Pulsara from '../../features/pulsara/page/Pulsara.page'
import FlagResolutionPage from '../../features/qaFlags/qaAddendums/page/FlagResolution.page'
import ReassignPage from '../../features/qaFlags/qaAddendums/page/Reassign.page'
import AssignmentHistoryPage from '../../features/qaFlags/qaAddendums/page/AssignmentHistory.page'
import ChartSummaryPageDetailPage from '../../features/qaFlags/qaAddendums/page/ChartSummaryPageDetail.page'
import ProtectedAuthenticatedRoute from './ProtectedAuthenticatedRoute.component'
import { setRouterHistory } from '../../utilities/navigation.util'

const AppRouter: React.FC = () => {
  const history = useHistory()
  const isHistoryInitialized = useRef(false)

  if (!isHistoryInitialized.current) {
    setRouterHistory(history)
    isHistoryInitialized.current = true
  }
  const user = useSelector(getUserSelector)
  const allowSecureRoutes = AuthenticationService.allowSecureRoutes(user)

  const oktaAuth = new OktaAuth({
    issuer: `https://${process.env.REACT_APP_DOMAIN}/oauth2/default`,
    clientId: `${process.env.REACT_APP_CLIENT_ID}`,
    redirectUri: `${window.location.origin}${AppRouterConstants.LOGIN_CALLBACK_ROUTE}`,
    pkce: true,
  })

  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) => {
    history.replace(toRelativeUrl(originalUri, window.location.origin))
  }

  const forcePwa = `${process.env.REACT_APP_FORCE_PWA_ON_MOBILE_BROWSERS}` === 'true'
  const showLandingPage = !forcePwa && !allowSecureRoutes && isMobileBrowser()

  if (showLandingPage) {
    return (
      <IonRouterOutlet id='main-now-content'>
        <Route path={AppRouterConstants.LANDING_PAGE_ROUTE} render={() => <Landing />} />
        <Route component={Landing} />
      </IonRouterOutlet>
    )
  }

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <IonRouterOutlet id='main-now-content'>
        <Route path={AppRouterConstants.LOGIN_CALLBACK_ROUTE} component={LoginCallback} />
        <Route path={AppRouterConstants.LOGIN_ROUTE} render={() => <Login />} />
        <Route path={AppRouterConstants.SPLASH_ROUTE} component={Splash} />
        <Route exact path={AppRouterConstants.INDEX_ROUTE} component={Login} />
        <Route path={AppRouterConstants.SERVICE_SELECTION_ROUTE} component={ServiceSelection} />

        {/* Authenticated routes (those below) should include a useEffect function that redirects users to the login page when allowSecureRoutes becomes false */}
        <Route path={AppRouterConstants.HOME_ROUTE} component={Home} />
        <Route path={AppRouterConstants.DEMO_ROUTE} component={Demo} />
        <Route path={AppRouterConstants.CREW_ROUTE} component={Crew} />
        <Route path={AppRouterConstants.TABS_ROUTE} component={Tabs} />
        <Route path={AppRouterConstants.DL_SCANNER_ROUTE} component={DriversLicenseScanner} />
        <Route path={AppRouterConstants.DOCUMENT_SCANNER_ROUTE} component={DocumentScanner} />
        <Route path={AppRouterConstants.SIGNATURE_ROUTE} component={SignaturesMainPage} />
        <Route path={AppRouterConstants.EKG_IMPORT_ROUTE} component={EKGImportMainPage} />
        <Route path={AppRouterConstants.SPECIAL_REPORTS_ROUTE} component={SpecialReportsPage} />
        <Route path={AppRouterConstants.FILLABLE_PDFS_ROUTE} component={FillablePDFsPage} />
        <Route path={AppRouterConstants.PULSARA_ROUTE} component={Pulsara} />
        <Route path={AppRouterConstants.TERMS_CONDITIONS_TAB} component={TermsAndConditions} />
        <Route path={AppRouterConstants.CAMERA_ATTACHMENTS_ROUTE} component={CameraAttachments} />
        <Route path={AppRouterConstants.HELP_ROUTE} component={HelpGuide} />
        <Redirect to={AppRouterConstants.LOGIN_ROUTE} />
        <ProtectedAuthenticatedRoute
          exact
          path={AppRouterConstants.QA_ADDENDUM_FLAG_RESOLUTION_ROUTE}
          component={FlagResolutionPage}
        />
        <ProtectedAuthenticatedRoute
          path={AppRouterConstants.QA_ADDENDUM_FLAG_REASSIGN_ROUTE}
          component={ReassignPage}
        />
        <ProtectedAuthenticatedRoute
          path={AppRouterConstants.QA_ADDENDUM_FLAG_HISTORY_ROUTE}
          component={AssignmentHistoryPage}
        />
        <ProtectedAuthenticatedRoute
          path={AppRouterConstants.QA_ADDENDUM_FLAG_CHART_SUMMARY_ROUTE}
          component={ChartSummaryPageDetailPage}
        />
      </IonRouterOutlet>
    </Security>
  )
}

export default AppRouter
